// App.js - Refactored to remove password handling
import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import config from './config';

// Component imports
import ParametersPanel from './components/ParametersPanel';

// Stage imports
import FileUploadStage from './stages/FileUploadStage';
import AnalysisTeaserStage from './stages/AnalysisTeaserStage';
import UsageQuestionsStage from './stages/UsageQuestionsStage';
import ContactFormStage from './stages/ContactFormStage';
import CodeVerificationStage from './stages/CodeVerificationStage';
import FinalReportStage from './stages/FinalReportStage';

// Password Protection Modal Component
const PasswordProtectionModal = ({ passwordError, onClose }) => {
  if (!passwordError) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Password-Protected File Detected</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="error-message">{passwordError.message}</p>

          {passwordError.suggestions && (
            <div className="instructions">
              <h4>How to remove password protection:</h4>
              <ol>
                {passwordError.suggestions.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {passwordError.fileFormat && (
            <p className="file-format">
              <small><strong>Detected format:</strong> {passwordError.fileFormat}</small>
            </p>
          )}

          <div className="tip-box">
            Your data is not stored. The file is held in memory only during analysis and is then discarded.
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ExcelAnalyzer = () => {
  // Stage management
  const [currentStage, setCurrentStage] = useState(1);
  
  // Data states
  const [analysisData, setAnalysisData] = useState({});
  const [userResponses, setUserResponses] = useState({});
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    preferredContact: 'email'
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  
  // Source tracking state
  const [sourceParam, setSourceParam] = useState(null);
  
  // UI states
  const [showParameters, setShowParameters] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Error state for password-protected files
  const [passwordError, setPasswordError] = useState(null);
  
  const fileInputRef = useRef(null);

  // Capture source parameter on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    if (source) {
      setSourceParam(source);
      console.log('📊 Source parameter detected:', source);
    }
  }, []);

  // Scoring parameters - adjustable for demos
  const [scoringParams, setScoringParams] = useState({
    worksheetWeight: 5,
    volumeWeight: 3,
    formulaWeight: 7,
    macroWeight: 9,
    timeEstimates: {
      simple: { min: 2, max: 4 },
      medium: { min: 6, max: 12 },
      complex: { min: 16, max: 24 }
    }
  });

  // Parameter management functions
  const updateParameter = (type, value) => {
    setScoringParams(prev => ({
      ...prev,
      [type + 'Weight']: parseInt(value)
    }));
  };

  const updateTimeEstimate = (level, value) => {
    const val = parseInt(value);
    let min, max;
    
    if (level === 'simple') {
      min = Math.max(1, val - 1);
      max = val + 1;
    } else if (level === 'medium') {
      min = Math.max(4, val - 3);
      max = val + 3;
    } else {
      min = Math.max(12, val - 4);
      max = val + 4;
    }
    
    setScoringParams(prev => ({
      ...prev,
      timeEstimates: {
        ...prev.timeEstimates,
        [level]: { min, max }
      }
    }));
  };

  // File handling functions
  const handleFileUpload = async (file) => {
    setIsAnalyzing(true);
    setPasswordError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scoringParams', JSON.stringify(scoringParams));

      const response = await fetch(`${config.apiBaseUrl}/api/analyze-excel`, {
        method: 'POST',
        body: formData
      });

      console.log('Response received:', response.status);
      console.log('Response headers:', [...response.headers.entries()]);

      const result = await response.json();
      console.log('Response parsed:', result);

      if (response.status === 423 && result.isPasswordProtected) {
        // Password-protected file detected - show instructions to disable protection
        setPasswordError({
          message: result.error || 'This file is password protected.',
          suggestions: result.suggestions || [
            'Open the file in Excel',
            'Go to File > Info > Protect Workbook > Encrypt with Password',
            'Delete the password (leave it blank) and click OK',
            'Save the file and upload it again'
          ],
          fileFormat: result.fileFormat
        });
        return;
      }

      if (!response.ok) {
        throw new Error(result.error || 'Analysis failed');
      }

      // Success - proceed to next stage
      setAnalysisData(result);
      setCurrentStage(2);
      setPasswordError(null);
    } catch (error) {
      // Handle other password-related errors that might not return 423 status
      if (error.message.includes('password') || error.message.includes('encrypted')) {
        setPasswordError({
          message: 'This file appears to be password protected. Please remove the password protection and try again.',
          suggestions: [
            'Open the file in Excel',
            'Go to File > Info > Protect Workbook > Encrypt with Password',
            'Delete the password (leave it blank) and click OK',
            'Save the file and upload it again'
          ]
        });
      } else {
        alert('Error analyzing file: ' + error.message);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Clear password error when user tries again
  const clearPasswordError = () => {
    setPasswordError(null);
  };

  // Stage navigation functions
  const showQuestions = () => {
    setCurrentStage(3);
  };

  const showContactForm = () => {
    setCurrentStage(4);
  };

  // Contact and verification functions
  const submitContactInfo = async () => {
    if (!contactInfo.name || !contactInfo.email) {
      alert('Please fill in your name and email address');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Sending analysis data:', analysisData);
      console.log('Analysis data keys:', Object.keys(analysisData));
      console.log('Filename from analysis:', analysisData.filename, analysisData.fileName);
      console.log('File format from analysis:', analysisData.fileFormat, analysisData.fileType);
      
      // Create lightweight version of analysis data for email (avoid 100MB+ payload)
      const lightweightAnalysisData = {
        fileName: analysisData.filename || analysisData.fileName,  // Handle both formats
        fileType: analysisData.fileFormat || analysisData.fileType,  // Handle both formats
        worksheetCount: analysisData.totalSheets || analysisData.worksheetCount,  // Handle both formats
        totalRows: analysisData.totalRows,
        totalColumns: analysisData.totalColumns,
        hasMacros: analysisData.hasMacros,
        formulaComplexity: analysisData.formulaComplexity,
        usagePattern: analysisData.usagePattern,
        crossReferences: analysisData.crossReferences,
        
        // Include the recommendation (this is what the user saw)
        recommendation: analysisData.recommendation,
        
        // Include user responses for context
        userResponses: analysisData.userResponses,
        
        // Include basic file info
        fileSize: analysisData.fileSize,
        totalCells: analysisData.totalCells,
        
        // Enhanced timestamp
        enhancedAt: analysisData.enhancedAt,
        
        // Simplified worksheet info (just counts, no raw data)
        worksheets: analysisData.fullAnalysis?.worksheets?.map(ws => ({
          name: ws.name,
          rows: ws.rows,
          columns: ws.columns,
          formulaCount: ws.formulas?.length || 0
        })) || []
      };
      
      console.log('Lightweight analysis data created:', lightweightAnalysisData);
      console.log('Lightweight fileName:', lightweightAnalysisData.fileName);
      console.log('Lightweight fileType:', lightweightAnalysisData.fileType);
      
      const response = await fetch(`${config.apiBaseUrl}/api/submit-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...contactInfo,
          analysisData: lightweightAnalysisData,  // Send lightweight version
          userResponses,
          source: sourceParam  // Include source parameter
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit contact information');
      }

      setContactEmail(result.email);
      setCurrentStage(5);
    } catch (error) {
      alert('Error submitting contact information: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      alert('Please enter the 6-digit verification code');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: contactEmail,
          code: verificationCode
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Verification failed');
      }

      setAnalysisData(result);
      setCurrentStage(6);
    } catch (error) {
      alert('Verification error: ' + error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  // Render stage components
  const renderCurrentStage = () => {
    switch (currentStage) {
      case 1:
        return (
          <FileUploadStage
            handleDrop={handleDrop}
            handleDragOver={handleDragOver}
            fileInputRef={fileInputRef}
            handleFileUpload={handleFileUpload}
            isAnalyzing={isAnalyzing}
          />
        );
      case 2:
        return (
          <AnalysisTeaserStage
            analysisData={analysisData}
            showQuestions={showQuestions}
          />
        );
      case 3:
        return (
          <UsageQuestionsStage
            userResponses={userResponses}
            setUserResponses={setUserResponses}
            analysisData={analysisData}
            setAnalysisData={setAnalysisData}
            showContactForm={showContactForm}
          />
        );
      case 4:
        return (
          <ContactFormStage
            contactInfo={contactInfo}
            setContactInfo={setContactInfo}
            submitContactInfo={submitContactInfo}
            isSubmitting={isSubmitting}
          />
        );
      case 5:
        return (
          <CodeVerificationStage
            contactEmail={contactEmail}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            verifyCode={verifyCode}
            isVerifying={isVerifying}
          />
        );
      case 6:
        return (
          <FinalReportStage
            analysisData={analysisData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="app">
      {/* Password Protection Modal */}
      <PasswordProtectionModal
        passwordError={passwordError}
        onClose={clearPasswordError}
      />

      {/* Main Content */}
      <div className="container">
        <div className="header">
          <h1>Scope the Modernization of a Business-Critical Spreadsheet</h1>
          <p>Upload your Excel file for a structured complexity assessment and a realistic migration path.</p>
        </div>

        {/* Render Current Stage */}
        {renderCurrentStage()}
      </div>
    </div>
  );
};

// Make sure to export the component properly
function App() {
  return <ExcelAnalyzer />;
}

export default App;